import {useMutation, UseMutationOptions, useQueryClient, InfiniteData} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpUpdateConversationMembers, UpdateConversationMembersPayload} from '../http/conversations.http'
import {Conversation, ConversationsList} from '@/features/conversations/types'
import {QUERY_KEYS} from '@/queryClient'
import {parseAxiosPromise} from '@/axiosInstance'
import {captureException} from '@sentry/react'

interface UpdateConversationMembersParams {
    conversationId: string
    payload: UpdateConversationMembersPayload
}

export const useUpdateConversationMembers = (
    patientId: string,
    options?: UseMutationOptions<Conversation, AxiosError, UpdateConversationMembersParams>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({conversationId, payload}: UpdateConversationMembersParams) =>
            parseAxiosPromise({
                axiosPromise: httpUpdateConversationMembers(conversationId, payload),
                onZodError: captureException,
                responseShape: Conversation
            }),
        onSuccess: (updatedConversation, variables, context) => {
            const {conversationId} = variables
            queryClient.setQueriesData(
                {
                    queryKey: [QUERY_KEYS.CONVERSATIONS, patientId],
                    exact: false
                },
                (oldData: InfiniteData<{data: ConversationsList}> | undefined) => {
                    if (!oldData) return oldData

                    const updatedPages = oldData.pages.map(page => ({
                        ...page,
                        data: {
                            ...page.data,
                            data: page.data.data.map(conversation =>
                                conversation.id === conversationId ? updatedConversation : conversation
                            )
                        }
                    }))

                    return {
                        ...oldData,
                        pages: updatedPages
                    }
                }
            )

            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.CONVERSATIONS, patientId]
            })

            options?.onSuccess?.(updatedConversation, variables, context)
        },
        onError: options?.onError,
        onMutate: options?.onMutate,
        onSettled: options?.onSettled
    })
}
